import { z } from 'zod';
import Decimal from 'decimal.js';
import { V } from 'helpers/validationMessages';

const positiveDecimal = (maxDecimals) =>
  z
    .string()
    .min(1, V.required)
    .refine(
      (v) => {
        try {
          const d = new Decimal(v);
          return !d.isNaN() && d.isPositive();
        } catch {
          return false;
        }
      },
      { message: V.positive },
    )
    .refine(
      (v) => {
        try {
          const parts = new Decimal(v).toString().split('.');
          return !parts[1] || parts[1].length <= maxDecimals;
        } catch {
          return false;
        }
      },
      { message: V.maxDecimals(maxDecimals) },
    );

const optionalNumericString = z
  .string()
  .optional()
  .refine(
    (v) => {
      if (!v) return true;
      try {
        const d = new Decimal(v);
        return !d.isNaN() && d.isPositive();
      } catch {
        return false;
      }
    },
    { message: V.positive },
  );

const productionMethodSchema = z.object({
  methodCode: z.string().min(1, V.required),
  name: z.string().optional(),
  steelMill: z.string().optional(),
  directParameters: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().optional(),
      valueType: z.string().optional(),
      value: z.string().optional(),
      description: z.string().optional(),
    }),
  ),
});

export const emissionsFormSchema = z
  .object({
    // Tab 1 — always required
    date: z.string().min(1, V.required),
    lastRecordDate: z.string().min(1, V.required),
    cnGood: z.string().min(1, V.required),
    operatorGoodName: z.string().optional(),
    specificDirectEmission: positiveDecimal(7),
    specificIndirectEmission: z.string(),
    indirectEmissionFactor: optionalNumericString,
    indirectEmissionFactorSourceValue: z.string().optional(),
    electricityConsumed: optionalNumericString,
    electricitySource: z.string().min(1, V.required),
    otherSourceIndication: z.string().optional(),
    shareDefaultEmissions: z.union([z.string(), z.number()]).optional(),
    typeOfApplicableReportingMethodology: z.string().min(1),
    indirectDeterminationType: z.string().min(1),
    indirectEmissionFactorSource: z.string().optional(),
    additionalInformation: z.string().optional(),
    // SEFA — operator only, always optional in schema; context enforces presence
    useBenchmarkDefault: z.boolean().optional(),
    benchmarkInputMode: z.string().optional(),
    benchmark: z.string().optional(),
    sefa: z.string().optional(),
    cbamFactor: z.union([z.string(), z.number()]).optional(),
    reportingYear: z.union([z.string(), z.number()]).optional(),
    // Tab 2
    productionMethods: z.array(productionMethodSchema).min(1, V.atLeastOne),
  })
  .superRefine((data, ctx) => {
    if (
      data.indirectDeterminationType === '01' &&
      data.indirectEmissionFactorSource === '01' &&
      !data.indirectEmissionFactor
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indirectEmissionFactor'],
        message: V.required,
      });
    }
    if (data.indirectDeterminationType === '01' && !data.electricityConsumed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['electricityConsumed'],
        message: V.required,
      });
    }
    if (data.typeOfApplicableReportingMethodology === 'TOM02' && !data.additionalInformation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['additionalInformation'],
        message: V.required,
      });
    }
    if (data.indirectEmissionFactorSource === '01' && !data.indirectEmissionFactorSourceValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indirectEmissionFactorSourceValue'],
        message: V.required,
      });
    }
  });

// Up to `maxDecimals` places, zero allowed; `required: false` also accepts a blank.
const nonNegativeDecimal = (maxDecimals, { required = true } = {}) =>
  z
    .string()
    .refine((v) => !required || Boolean(v), { message: V.required })
    .refine(
      (v) => {
        if (!v) return true;
        try {
          const d = new Decimal(v);
          return !d.isNaN() && !d.isNegative();
        } catch {
          return false;
        }
      },
      { message: V.nonNegative },
    )
    .refine(
      (v) => {
        if (!v) return true;
        try {
          const parts = new Decimal(v).toString().split('.');
          return !parts[1] || parts[1].length <= maxDecimals;
        } catch {
          return false;
        }
      },
      { message: V.maxDecimals(maxDecimals) },
    );

/**
 * The UK Emission Factor form (backend docs/features/uk-cbam-emission-factors, D11–D13):
 * CN code, product, dates, a direct intensity that may be zero and an optional indirect.
 * `passthrough` keeps the form's other values in place; the UK submit sends none of them.
 */
export const ukEmissionsFormSchema = z
  .object({
    date: z.string().min(1, V.required),
    lastRecordDate: z.string().min(1, V.required),
    cnGood: z.string().min(1, V.required),
    operatorGoodName: z.string().optional(),
    specificDirectEmission: nonNegativeDecimal(7),
    specificIndirectEmission: nonNegativeDecimal(7, { required: false }),
  })
  .passthrough();
